/**
 * WebSocket types for CGIC TransferHub real-time updates
 *
 * These types match the backend's actual message format:
 *   {"type":"broadcast","topic":"partner_status_changes","data":{...},"timestamp":"..."}
 */

/** WebSocket connection status */
export type WebSocketStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'reconnecting';

/**
 * Broadcast topic names from the backend — the COMPLETE set.
 *
 * THUB-182: this union used to end in `| string`, which collapsed the whole
 * union to `string` and silently removed all compile-time checking, so a typo'd
 * topic in a `subscribe()` call compiled fine and simply never fired. These
 * seven are the only topics the backend emits (see the topic argument to
 * `broadcast_update` in shared/websocket_utils.py), and server-side filtering
 * is by topic only, so an unknown topic can never arrive.
 */
export type WebSocketTopic =
  | 'partner_status_changes'
  | 'connection_status_changes'
  | 'task_status_changes'
  | 'execution_updates'
  | 'analytics_updates'
  | 'role_mapping_changes'
  | 'policy_changes';

/**
 * Message format the backend actually sends.
 *
 * `topic` is intentionally a plain `string`, not `WebSocketTopic`: this is the
 * shape of UNVALIDATED data straight off the wire, so it must be able to hold a
 * topic this client doesn't know about (e.g. a newer backend). The narrow union
 * is enforced on the OUTBOUND side — where we choose what to subscribe to.
 */
export interface BroadcastMessage {
  type: 'broadcast';
  topic: string;
  data: any;
  timestamp: string;
}

/** Heartbeat/pong messages from backend */
export interface HeartbeatMessage {
  type: 'heartbeat' | 'pong';
  timestamp?: string;
}

/** Any message from the backend */
export type ServerMessage = BroadcastMessage | HeartbeatMessage | { type: string; [key: string]: any };

/** Callback type for topic subscriptions */
export type TopicCallback = (data: any) => void;

/** WebSocket context value exposed to consumers */
export interface WebSocketContextValue {
  /** Whether WebSocket is currently connected */
  isConnected: boolean;
  /** Detailed connection status */
  connectionStatus: WebSocketStatus;
  /** Number of reconnect attempts */
  reconnectAttempts: number;
  /** Subscribe to a broadcast topic. Returns unsubscribe function. */
  subscribe: (topic: string, callback: TopicCallback) => () => void;
  /** Manually trigger reconnection */
  reconnect: () => void;
  /** Explicitly close the WebSocket connection (e.g. on logout) */
  disconnect: () => void;
}

/** Reconnection configuration */
export interface ReconnectionConfig {
  /** Initial delay before first reconnect attempt (ms) */
  initialDelay: number;
  /** Maximum delay between reconnect attempts (ms) */
  maxDelay: number;
  /** Multiplier for exponential backoff */
  backoffMultiplier: number;
  /** Maximum number of reconnect attempts before giving up */
  maxAttempts: number;
}

/** Default reconnection configuration */
export const DEFAULT_RECONNECTION_CONFIG: ReconnectionConfig = {
  initialDelay: 1000,
  maxDelay: 8000,
  backoffMultiplier: 2,
  maxAttempts: 10,
};

/** WebSocket connection state (internal) */
export interface WebSocketState {
  status: WebSocketStatus;
  reconnectAttempts: number;
  lastConnectedAt: Date | null;
  lastDisconnectedAt: Date | null;
}

/**
 * Type guard for broadcast messages from the backend.
 * Accepts the backend's actual format: {"type":"broadcast","topic":"...","data":{...}}
 */
export function isBroadcastMessage(value: unknown): value is BroadcastMessage {
  if (typeof value !== 'object' || value === null) return false;
  const msg = value as Record<string, unknown>;
  return (
    msg.type === 'broadcast' &&
    typeof msg.topic === 'string' &&
    msg.data !== undefined
  );
}

/**
 * Type guard for heartbeat/pong messages
 */
export function isHeartbeatMessage(value: unknown): value is HeartbeatMessage {
  if (typeof value !== 'object' || value === null) return false;
  const msg = value as Record<string, unknown>;
  return msg.type === 'heartbeat' || msg.type === 'pong';
}

// ============================================================================
// Payload types (kept for future use by consumers)
// ============================================================================

/** File processing event payload */
export interface FileProcessingPayload {
  fileId: string;
  filename: string;
  partnerId: string;
  taskId: string;
  stage: 'landing' | 'processing' | 'validation' | 'archive' | 'notification' | 'delivery' | 'pickup' | 'quarantine';
  status: 'started' | 'completed' | 'failed';
  s3Key?: string;
  errorMessage?: string;
}

/** Task execution event payload */
export interface TaskExecutionPayload {
  executionId: string;
  taskId: string;
  partnerId: string;
  status: 'started' | 'running' | 'completed' | 'failed';
  startTime: string;
  endTime?: string;
  filesProcessed?: number;
  error?: string;
}

/** Connection test event payload */
export interface ConnectionTestPayload {
  testId: string;
  connectionId: string;
  stage: 'connecting' | 'authenticating' | 'listing' | 'permissions';
  status: 'started' | 'success' | 'failed';
  duration?: number;
  error?: string;
}

/** System alert payload */
export interface SystemAlertPayload {
  alertId: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  source: string;
  acknowledgedAt?: string;
}

// ============================================================================
// Legacy type aliases (kept for backward compatibility during migration)
// ============================================================================

/** @deprecated Use WebSocketTopic instead */
export type WebSocketEventType = string;

/** @deprecated Use string instead */
export type WebSocketEntityType = string;

/** @deprecated Use string instead */
export type EntityAction = 'created' | 'updated' | 'deleted';

/** @deprecated Use BroadcastMessage instead */
export interface WebSocketMessage<T = unknown> {
  type: string;
  topic?: string;
  entityType?: string;
  entityId?: string;
  action?: string;
  payload?: T;
  data?: any;
  timestamp: string;
  version?: number;
}
